<template>
  <AutopilotOverlay v-slot="ap">
    <div
      class="autopilot-bar tw:flex tw:h-dvh tw:w-full tw:flex-col tw:border-r tw:border-[#edeeef] tw:bg-white tw:md:w-[400px]"
      :class="ap.isOpen ? 'autopilot-bar-open tw:pointer-events-auto' : 'tw:pointer-events-none'">
      <!-- header -->
      <div class="tw:flex tw:h-[60px] tw:shrink-0 tw:items-center tw:gap-3 tw:border-b tw:border-[#edeeef] tw:px-4">
        <span class="tw:flex tw:size-9 tw:shrink-0 tw:items-center tw:justify-center">
          <AutopilotOrb :size="36" :active="ap.isConnecting || ap.isStreaming" label="Autopilot" />
        </span>
        <div class="tw:min-w-0 tw:flex-1">
          <div class="tw:text-sm tw:font-semibold tw:text-gray-900">Autopilot</div>
          <div class="tw:truncate tw:text-xs tw:text-gray-500">{{ headerSubtitle(ap) }}</div>
        </div>
        <button
          type="button"
          :class="[iconBtnClass, showDebugTabs ? 'tw:bg-primary-lt tw:text-primary' : '']"
          title="Debug"
          @click="toggleDebugTabs">
          <IconBug :size="16" />
        </button>
        <button
          v-if="ap.hasConversation"
          type="button"
          :class="iconBtnClass"
          title="Reset conversation"
          @click="onReset(ap)">
          <IconTrash :size="16" />
        </button>
        <button type="button" :class="iconBtnClass" title="Close" @click="ap.close()">
          <IconChevronsLeft :size="16" />
        </button>
      </div>

      <!-- context chip -->
      <div
        v-if="contextChip"
        class="tw:flex tw:shrink-0 tw:items-center tw:gap-2 tw:border-b tw:border-[#edeeef] tw:bg-[#F7F7F7] tw:px-4 tw:py-2">
        <IconTarget :size="14" class="tw:shrink-0 tw:text-gray-400" />
        <span class="tw:min-w-0 tw:truncate tw:text-xs tw:text-gray-700">
          <template v-if="contextChip.kind">{{ contextChip.kind }}&nbsp;</template>
          <span class="tw:font-medium tw:text-gray-900">{{ contextChip.title }}</span>
        </span>
        <span class="tw:ml-auto tw:shrink-0 tw:text-[11px] tw:text-gray-500">automatically detected</span>
      </div>

      <div v-if="showDebugTabs" class="tw:shrink-0 tw:px-4">
        <Tabs :tabs="autopilotTabs" v-model="activeTab" class="nav-bordered sticky-top bg-white"></Tabs>
      </div>

      <!-- chat -->
      <div v-if="activeTab === 'messages'" class="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col">
        <div
          ref="messagesListRef"
          class="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:gap-4 tw:overflow-y-auto tw:px-4 tw:py-4">
          <LeadAlert v-if="ap.connectionError" variant="error" :title="ap.connectionErrorTitle || 'Connection error'">
            {{ ap.connectionError.message }}
            <template #actions>
              <LeadButton variant="outline" size="sm" @click="ap.retryConnection()">Retry</LeadButton>
            </template>
          </LeadAlert>

          <LeadAlert v-if="interactionError" variant="error" :title="interactionError.title">
            {{ interactionError.message }}
            <pre
              v-if="interactionError.detail"
              class="tw:mt-2! tw:mb-0! tw:max-h-32 tw:overflow-auto tw:rounded tw:bg-white tw:p-2 tw:text-xs tw:text-red-800 tw:whitespace-pre-wrap"
              >{{ interactionError.detail }}</pre>
            <template #actions>
              <LeadButton
                v-if="ap.messageText.trim() && ap.isConnected"
                variant="outline"
                size="sm"
                @click="onSend(ap)">
                Try again
              </LeadButton>
              <LeadButton variant="outline" size="sm" @click="clearInteractionError()">Dismiss</LeadButton>
            </template>
          </LeadAlert>

          <template v-if="showEmptyState(ap)">
            <div
              class="tw:flex tw:flex-col tw:items-start tw:gap-2 tw:rounded-lg tw:border tw:border-[#edeeef] tw:bg-[#F7F7F7] tw:p-4">
              <span
                class="tw:flex tw:size-9 tw:items-center tw:justify-center tw:rounded-lg tw:bg-primary tw:text-white">
                <IconSparkles :size="20" />
              </span>
              <div class="tw:text-base tw:font-semibold tw:text-gray-900">How can I help?</div>
              <p class="tw:m-0 tw:text-[13px] tw:leading-5 tw:text-gray-600">
                <template v-if="contextChip">
                  I can see you're on <span class="tw:font-medium tw:text-gray-900">{{ contextChip.title }}</span
                  >. Ask me anything about this page, or pick a suggestion.
                </template>
                <template v-else>Ask me anything about this page, or pick a suggestion.</template>
              </p>
            </div>

            <div class="tw:flex tw:items-center tw:gap-2">
              <span class="tw:text-[11px] tw:font-medium tw:tracking-wider tw:text-gray-500">SUGGESTIONS</span>
              <span class="tw:h-px tw:flex-1 tw:bg-[#edeeef]"></span>
              <span class="tw:text-xs tw:text-gray-500">{{ suggestions.length }}</span>
            </div>

            <div class="tw:flex tw:flex-col tw:gap-2">
              <button
                v-for="suggestion in suggestions"
                :key="suggestion.prompt"
                type="button"
                class="tw:flex tw:w-full tw:cursor-pointer tw:items-center tw:gap-3 tw:rounded-none! tw:border tw:border-[#edeeef] tw:bg-white tw:px-3 tw:py-3 tw:text-left tw:hover:bg-[#F7F7F7] tw:disabled:cursor-not-allowed tw:disabled:opacity-50 tw:disabled:hover:bg-white"
                :disabled="!ap.isConnected"
                @click="sendSuggestion(ap, suggestion.prompt)">
                <span class="tw:min-w-0 tw:flex-1 tw:text-sm tw:font-medium tw:text-gray-900">{{
                  suggestion.label
                }}</span>
                <IconChevronRight :size="16" class="tw:shrink-0 tw:text-gray-400" />
              </button>
            </div>

            <div class="tw:mt-auto tw:rounded-lg tw:border tw:border-dashed tw:border-gray-300 tw:bg-[#F7F7F7] tw:p-3">
              <div class="tw:flex tw:items-center tw:gap-2 tw:text-[13px] tw:text-gray-600">
                <IconBulb :size="15" class="tw:text-gray-400" />
                Autopilot will speak up when something stands out.
              </div>
            </div>
          </template>

          <template v-else>
            <div class="tw:flex tw:flex-col tw:gap-4">
              <component
                :is="getMessageComponent(message)"
                v-for="(message, index) in ap.messages"
                :key="`${message.created_at}-${index}`"
                :message="message"
                :is-streaming="ap.isStreaming && index === ap.messages.length - 1" />

              <div
                v-if="ap.isConnecting"
                class="tw:flex tw:flex-col tw:items-center tw:justify-center tw:gap-3"
                :class="ap.messageCount ? 'tw:py-4' : 'tw:py-10'">
                <AutopilotOrb :size="ap.messageCount ? 40 : 56" :active="true" label="Connecting" />
                <div class="tw:text-xs tw:text-gray-500">Connecting…</div>
              </div>
            </div>
          </template>

          <div v-if="ap.pendingConfirmations.length" class="tw:flex tw:flex-col tw:gap-3">
            <ConfirmationPrompt
              v-for="pending in ap.pendingConfirmations"
              :key="pending.id"
              :pending="pending"
              v-slot="{ displayName, argumentsJson }">
              <div class="tw:rounded-lg tw:border tw:border-orange-200 tw:bg-orange-50 tw:p-3">
                <div class="tw:flex tw:items-center tw:gap-2">
                  <IconAlertTriangle :size="16" class="tw:shrink-0 tw:text-orange-600" />
                  <span class="tw:text-sm tw:font-semibold tw:text-gray-900">Approval required</span>
                  <span
                    class="tw:ml-auto tw:inline-flex tw:items-center tw:rounded-full tw:border tw:border-orange-200 tw:bg-orange-50 tw:px-2 tw:py-px tw:text-xs tw:font-medium tw:text-orange-600"
                    >Sensitive</span
                  >
                </div>
                <p class="tw:m-0 tw:mt-2 tw:text-[13px] tw:leading-5 tw:text-gray-700">
                  Autopilot wants to run <span class="tw:font-medium">{{ displayName }}</span
                  >. Confirm before it continues.
                </p>
                <details class="tw:mt-2">
                  <summary class="tw:cursor-pointer tw:text-xs tw:text-gray-500">Show arguments</summary>
                  <pre
                    class="tw:mt-1 tw:mb-0 tw:overflow-auto tw:rounded tw:bg-white tw:p-2 tw:text-xs tw:text-gray-600"
                    >{{ argumentsJson }}</pre>
                </details>
              </div>
            </ConfirmationPrompt>
          </div>

          <div v-if="ap.pendingAskUser.length">
            <AskUserPrompt
              :prompts="ap.pendingAskUser"
              v-slot="{
                prompts,
                active,
                activeIndex,
                isLastTab,
                allAnswered,
                isSubmitting,
                textDraft,
                boolDraft,
                radioDraft,
                checklistDraft,
                isAnswered,
                setActiveIndex,
                advance,
                send,
              }">
              <div class="tw:rounded-lg tw:border tw:border-[#edeeef] tw:bg-white tw:p-3 tw:shadow-sm">
                <ul v-if="prompts.length > 1" class="tw:mb-3 tw:flex tw:gap-1 tw:p-0">
                  <li v-for="(q, i) in prompts" :key="q.id" class="tw:list-none">
                    <button
                      type="button"
                      class="tw:inline-flex tw:size-7 tw:items-center tw:justify-center tw:rounded-md tw:border tw:text-xs"
                      :class="
                        i === activeIndex
                          ? 'tw:border-primary tw:bg-primary-lt tw:text-primary'
                          : 'tw:border-[#edeeef] tw:bg-white tw:text-gray-600'
                      "
                      @click="setActiveIndex(i)">
                      {{ Number(i) + 1 }}
                    </button>
                  </li>
                </ul>

                <div class="tw:mb-2 tw:text-sm tw:text-gray-900">{{ active.question }}</div>

                <textarea
                  v-if="active.type === 'text' && active.arguments?.multiline"
                  class="tw:w-full tw:rounded-lg tw:border tw:border-[#D0D5DD] tw:px-3 tw:py-2 tw:text-sm tw:shadow-xs tw:outline-none tw:focus:border-primary"
                  rows="3"
                  :placeholder="active.arguments?.placeholder"
                  v-model="textDraft[active.id]"></textarea>
                <LeadInput
                  v-else-if="active.type === 'text'"
                  :placeholder="active.arguments?.placeholder"
                  v-model="textDraft[active.id]" />

                <div v-else-if="active.type === 'bool'" class="tw:flex tw:gap-2">
                  <LeadButton
                    :variant="boolDraft[active.id] === true ? 'primary' : 'primary-outline'"
                    @click="boolDraft[active.id] = true">
                    {{ active.arguments?.yes_label || 'Yes' }}
                  </LeadButton>
                  <LeadButton
                    :variant="boolDraft[active.id] === false ? 'primary' : 'primary-outline'"
                    @click="boolDraft[active.id] = false">
                    {{ active.arguments?.no_label || 'No' }}
                  </LeadButton>
                </div>

                <div v-else-if="active.type === 'radiolist'" class="tw:flex tw:flex-col tw:gap-1">
                  <label
                    v-for="opt in active.arguments?.options || []"
                    :key="opt.value"
                    class="tw:flex tw:items-center tw:gap-2 tw:text-sm">
                    <input type="radio" :value="opt.value" v-model="radioDraft[active.id]" />
                    {{ opt.label }}
                  </label>
                </div>

                <div v-else-if="active.type === 'checklist'" class="tw:flex tw:flex-col tw:gap-1">
                  <label
                    v-for="opt in active.arguments?.options || []"
                    :key="opt.value"
                    class="tw:flex tw:items-center tw:gap-2 tw:text-sm">
                    <input type="checkbox" :value="opt.value" v-model="checklistDraft[active.id]" />
                    {{ opt.label }}
                  </label>
                </div>

                <div class="tw:mt-3">
                  <LeadButton
                    v-if="!isLastTab"
                    variant="primary-outline"
                    :disabled="!isAnswered(active)"
                    @click="advance()">
                    Next
                  </LeadButton>
                  <LeadButton v-else variant="primary" :disabled="!allAnswered" :loading="isSubmitting" @click="send()">
                    Send answer
                  </LeadButton>
                </div>
              </div>
            </AskUserPrompt>
          </div>

          <div v-if="ap.isStreaming" class="tw:mt-auto tw:flex tw:items-center tw:gap-2.5 tw:self-start">
            <button type="button" :class="iconBtnClass" title="Stop" @click="ap.stopStreaming()">
              <IconPlayerStop :size="16" />
            </button>
            <AutopilotOrb :size="40" :active="true" label="Thinking" />
            <span class="tw:text-xs tw:text-gray-500">Thinking…</span>
          </div>
        </div>

        <!-- approval footer -->
        <div
          v-if="ap.pendingConfirmations.length"
          class="tw:flex tw:shrink-0 tw:flex-col tw:gap-2 tw:border-t tw:border-[#edeeef] tw:px-4 tw:py-3">
          <ConfirmationPrompt :pending="ap.pendingConfirmations[0]" v-slot="{ allow, alwaysAllow, deny }">
            <div class="tw:flex tw:w-full tw:flex-col tw:gap-2">
              <LeadButton variant="primary" class="tw:w-full" :icon="IconCheck" @click="allow()">
                Allow once
              </LeadButton>
              <div class="tw:flex tw:gap-2">
                <LeadButton variant="primary-outline" class="tw:w-full" :icon="IconChecks" @click="alwaysAllow()">
                  Always allow
                </LeadButton>
                <LeadButton variant="white" class="tw:w-full" @click="deny()">Deny</LeadButton>
              </div>
            </div>
          </ConfirmationPrompt>
        </div>

        <!-- composer -->
        <div v-else-if="!ap.pendingAskUser.length" class="tw:shrink-0 tw:border-t tw:border-[#edeeef] tw:px-4 tw:py-3">
          <button
            v-if="showCompactComposer(ap)"
            type="button"
            class="tw:flex tw:h-9 tw:w-full tw:items-center tw:gap-2 tw:rounded-lg tw:border tw:border-[#D0D5DD] tw:bg-white tw:px-3 tw:text-left tw:shadow-xs"
            @click="expandComposer()">
            <IconSparkles :size="15" class="tw:text-gray-400" />
            <span class="tw:text-sm tw:text-gray-400">Or ask me something…</span>
          </button>

          <div v-else class="tw:rounded-lg tw:border tw:border-[#D0D5DD] tw:bg-white tw:p-2 tw:shadow-xs">
            <textarea
              :value="ap.messageText"
              class="tw:w-full tw:resize-none! tw:border-0 tw:bg-transparent tw:p-1 tw:text-sm tw:leading-6 tw:text-gray-900 tw:outline-none tw:placeholder:text-gray-400"
              placeholder="Ask Autopilot…"
              rows="2"
              :disabled="ap.isSending || !ap.isConnected"
              @input="ap.setMessageText(($event.target as HTMLTextAreaElement).value)"
              @keydown.enter.exact.prevent="onSend(ap)"
              @focus="composerFocused = true"
              @blur="onComposerBlur(ap)"
              ref="messageInput"></textarea>
            <div class="tw:mt-1 tw:flex tw:items-center tw:justify-end">
              <button
                type="button"
                class="tw:inline-flex tw:size-8 tw:items-center tw:justify-center tw:rounded-lg tw:border-0 tw:bg-primary tw:text-white tw:disabled:opacity-35"
                :disabled="!ap.canSend"
                title="Send"
                @click="onSend(ap)">
                <IconSend v-if="!ap.isSending" :size="16" />
                <IconLoader2 v-else class="ap-spin" :size="16" />
              </button>
            </div>
          </div>

          <div class="tw:mt-2 tw:flex tw:items-center tw:gap-1.5 tw:text-[11px] tw:text-gray-500">
            <IconLock :size="12" />
            Autopilot asks before it changes, books, or sends anything.
          </div>
        </div>
      </div>

      <!-- debug: functions -->
      <div
        v-else-if="activeTab === 'functions'"
        class="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:overflow-y-auto tw:p-4">
        <h6 class="tw:mb-2 tw:text-sm tw:font-semibold">Registered Functions</h6>
        <div v-if="store.registeredFunctions.length === 0" class="tw:text-xs tw:text-gray-500">
          No functions registered
        </div>
        <div v-else class="tw:flex tw:flex-col tw:gap-2">
          <div
            v-for="func in store.registeredFunctions"
            :key="func.name"
            class="tw:rounded-lg tw:border tw:border-[#edeeef] tw:bg-[#F7F7F7] tw:p-2">
            <div class="tw:text-sm tw:font-semibold">{{ func.name }}</div>
            <div v-if="func.description" class="tw:mt-1 tw:text-xs tw:text-gray-500">{{ func.description }}</div>
          </div>
        </div>

        <h6 class="tw:mt-6 tw:mb-2 tw:text-sm tw:font-semibold">Synced State</h6>
        <div v-if="Object.keys(store.context).length === 0" class="tw:text-xs tw:text-gray-500">No synced state</div>
        <pre
          v-else
          class="tw:m-0 tw:max-h-[300px] tw:overflow-auto tw:rounded-lg tw:bg-[#F7F7F7] tw:p-3 tw:text-xs tw:break-all tw:whitespace-pre-wrap"
          >{{ formattedContext }}</pre>
      </div>

      <!-- debug: status -->
      <div
        v-else-if="activeTab === 'status'"
        class="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:overflow-y-auto tw:p-4">
        <h6 class="tw:mb-2 tw:text-sm tw:font-semibold">Connection Status</h6>
        <div class="tw:flex tw:items-center tw:gap-2">
          <div
            class="tw:size-2 tw:shrink-0 tw:rounded-full"
            :class="{
              'tw:bg-green-500': store.isConnected,
              'tw:bg-amber-400 tw:animate-pulse': store.isConnecting,
              'tw:bg-red-500': store.isDisconnected,
            }"></div>
          <span class="tw:text-sm">{{ statusText }}</span>
        </div>

        <div class="tw:mt-6 tw:flex tw:items-center tw:justify-between tw:mb-2">
          <h6 class="tw:m-0 tw:text-sm tw:font-semibold">Recent Activity</h6>
          <LeadButton v-if="store.recentActivity.length > 0" variant="white" @click="store.clearActivity()">
            Clear
          </LeadButton>
        </div>
        <div v-if="store.recentActivity.length === 0" class="tw:text-xs tw:text-gray-500">No recent activity</div>
        <div v-else class="tw:flex tw:flex-col tw:gap-2">
          <div
            v-for="activity in store.recentActivity"
            :key="activity.id"
            class="tw:rounded-r-lg tw:border-l-4 tw:bg-[#F7F7F7] tw:p-2"
            :class="{
              'tw:border-green-500': activity.type === 'success',
              'tw:border-red-500': activity.type === 'error',
              'tw:border-sky-500': activity.type === 'call',
              'tw:border-gray-400': activity.type === 'info',
            }">
            <div class="tw:mb-1 tw:text-xs tw:text-gray-500">{{ formatTime(activity.timestamp) }}</div>
            <div class="tw:text-sm">{{ activity.message }}</div>
          </div>
        </div>
      </div>
    </div>
  </AutopilotOverlay>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import {
  IconAlertTriangle,
  IconBug,
  IconBulb,
  IconCheck,
  IconChecks,
  IconChevronRight,
  IconChevronsLeft,
  IconLoader2,
  IconLock,
  IconPlayerStop,
  IconSend,
  IconSparkles,
  IconTarget,
  IconTrash,
} from '@tabler/icons-vue';
import { AutopilotOverlay, ConfirmationPrompt, AskUserPrompt, useAutopilotStore } from '@llmor/autopilot-vue';
import type { ConversationMessage as ConversationMessageType } from '@llmor/autopilot-core';
import { Tabs } from '@prospective/lithium';
import LeadAlert from '@/components/lead-design/LeadAlert.vue';
import LeadButton from '@/components/lead-design/LeadButton.vue';
import LeadInput from '@/components/form/LeadInput.vue';
import { useAutopilotRuntimeError } from '@/composables/useAutopilotRuntimeError';
import AutopilotOrb from './AutopilotOrb.vue';
import ConversationMessage from './ConversationMessage.vue';
import ConversationMessageAssistant from './ConversationMessageAssistant.vue';

type OverlaySlot = {
  isOpen: boolean;
  messages: ConversationMessageType[];
  hasConversation: boolean;
  messageCount: number;
  isStreaming: boolean;
  connectionError: { message: string } | null;
  connectionErrorTitle: string;
  isConnected: boolean;
  isConnecting: boolean;
  pendingConfirmations: { id: string }[];
  pendingAskUser: unknown[];
  messageText: string;
  isSending: boolean;
  canSend: boolean;
  setMessageText: (value: string) => void;
  sendMessage: () => Promise<boolean>;
  stopStreaming: () => Promise<void>;
  resetConversation: () => Promise<void>;
  retryConnection: () => Promise<void>;
  close: () => void;
};

const store = useAutopilotStore();
const {
  error: interactionError,
  clear: clearInteractionError,
  capture: captureInteractionError,
} = useAutopilotRuntimeError();
const activeTab = ref('messages');
const messageInput = ref<HTMLTextAreaElement>();
const messagesListRef = ref<HTMLDivElement>();
const showDebugTabs = ref(false);
const composerFocused = ref(false);

const iconBtnClass =
  'tw:inline-flex tw:size-8 tw:items-center tw:justify-center tw:rounded-lg tw:border-0 tw:bg-transparent tw:text-gray-500 tw:transition tw:hover:bg-gray-100 tw:hover:text-gray-900';

const suggestions = [
  { label: 'What’s on this page?', prompt: 'What’s on this page?' },
  { label: 'What can you do here?', prompt: 'What can you do here?' },
];

const autopilotTabs = computed(() => [
  {
    label: 'Chat',
    value: 'messages',
    badge: store.messageCount > 0 ? store.messageCount.toString() : undefined,
  },
  {
    label: 'Status',
    value: 'status',
    badge: undefined,
  },
  {
    label: 'Functions',
    value: 'functions',
    badge: store.registeredFunctions.length > 0 ? store.registeredFunctions.length.toString() : undefined,
  },
]);

const statusText = computed(() => {
  switch (store.connectionStatus) {
    case 'connected':
      return 'Connected';
    case 'connecting':
      return 'Connecting...';
    case 'disconnected':
      return 'Disconnected';
    default:
      return 'Unknown';
  }
});

const formattedContext = computed(() => JSON.stringify(store.context, null, 2));

const contextChip = computed<{ kind: string | null; title: string } | null>(() => {
  const ctx = store.context as Record<string, any>;
  const order = ctx.current_order;
  if (order?.title) {
    return { kind: 'Order', title: order.title };
  }
  const header = ctx.page_header;
  if (header?.title) {
    return { kind: null, title: header.title };
  }
  const selectedName = ctx.current_page?.selectedNode?.name;
  if (selectedName) {
    return { kind: null, title: selectedName };
  }
  return null;
});

function headerSubtitle(ap: OverlaySlot): string {
  if (ap.isConnecting) return 'Connecting…';
  if (ap.connectionError) return ap.connectionErrorTitle || 'Cannot reach the server';
  if (interactionError.value) return interactionError.value.title;
  if (ap.pendingConfirmations.length) return 'Waiting for your approval';
  if (ap.messageCount === 0) return 'Here for the first time';
  if (contextChip.value?.title) return `Working on ${contextChip.value.title}`;
  return 'Working on this page';
}

function showEmptyState(ap: OverlaySlot): boolean {
  return (
    ap.messageCount === 0 &&
    !ap.isStreaming &&
    !ap.pendingConfirmations.length &&
    !ap.pendingAskUser.length &&
    !ap.isConnecting &&
    !ap.connectionError
  );
}

function showCompactComposer(ap: OverlaySlot): boolean {
  return showEmptyState(ap) && !composerFocused.value && !ap.messageText;
}

function toggleDebugTabs(): void {
  showDebugTabs.value = !showDebugTabs.value;
  if (!showDebugTabs.value) {
    activeTab.value = 'messages';
  }
}

function formatTime(timestamp: number): string {
  return new Date(timestamp).toLocaleTimeString();
}

function getMessageComponent(message: ConversationMessageType): any {
  switch (message.role) {
    case 'assistant':
      return ConversationMessageAssistant;
    default:
      return ConversationMessage;
  }
}

async function onSend(ap: OverlaySlot): Promise<void> {
  clearInteractionError();
  const sent = await ap.sendMessage();
  if (sent) {
    composerFocused.value = true;
    await nextTick();
    messageInput.value?.focus();
  }
}

async function onReset(ap: OverlaySlot): Promise<void> {
  clearInteractionError();
  try {
    await ap.resetConversation();
  } catch (thrown) {
    captureInteractionError(thrown);
  }
}

async function sendSuggestion(ap: OverlaySlot, text: string): Promise<void> {
  ap.setMessageText(text);
  await nextTick();
  await onSend(ap);
}

async function expandComposer(): Promise<void> {
  composerFocused.value = true;
  await nextTick();
  messageInput.value?.focus();
}

function onComposerBlur(ap: OverlaySlot): void {
  if (!ap.messageText) {
    composerFocused.value = false;
  }
}

async function scrollToBottom(): Promise<void> {
  await nextTick();
  const list = messagesListRef.value;
  if (list) {
    list.scrollTop = list.scrollHeight;
  }
}

watch([activeTab, () => store.hasConversation], async ([tab, hasConv]) => {
  if (tab === 'messages' && hasConv && store.isBarOpen) {
    await nextTick();
    messageInput.value?.focus();
  }
});

watch([() => store.visibleMessages.length, () => store.lastMessageUpdate], async () => {
  if (activeTab.value === 'messages') {
    await scrollToBottom();
  }
});

watch(
  () => store.isBarOpen,
  async (isOpen) => {
    if (isOpen && activeTab.value === 'messages') {
      await scrollToBottom();
    }
    if (!isOpen) {
      composerFocused.value = false;
    }
  }
);
</script>

<style scoped>
.autopilot-bar {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1050;
  transform: translateX(-100%);
  transition: transform 0.3s ease-in-out;
  box-shadow: none;
}

.autopilot-bar-open {
  transform: translateX(0);
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.12);
}

.ap-spin {
  animation: apspin 1.1s linear infinite;
  transform-origin: 50% 50%;
}

@keyframes apspin {
  to {
    transform: rotate(360deg);
  }
}
</style>
